import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Receipt, TrendingDown } from 'lucide-react';
import AdminLayout from '../../../layouts/AdminLayout';
import { operationalCostsApi } from '../../../services/api';
import { formatCurrency, formatDate } from '../../../utils/helpers';
import { useLanguage } from '../../../contexts/LanguageContext';

const CATEGORY_LABELS = {
  bahan_baku: 'Bahan Baku', listrik: 'Listrik', air: 'Air',
  sewa: 'Sewa', gaji: 'Gaji', peralatan: 'Peralatan', lain: 'Lain-lain',
};
const COLORS = ['#ef4444', '#3b82f6', '#f59e0b', '#0f6c44', '#8b5cf6', '#06b6d4', '#84cc16'];

export default function CostsReportPage() {
  const { t } = useLanguage();
  const [period, setPeriod] = useState('month');

  const { data: costs = [], isLoading } = useQuery({
    queryKey: ['operational-costs'],
    queryFn: () => operationalCostsApi.list().then(r => r.data),
  });

  const now = new Date();
  const filtered = costs.filter(c => {
    const d = new Date(c.created_at);
    if (period === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    if (period === 'year') return d.getFullYear() === now.getFullYear();
    return true;
  });

  const total = filtered.reduce((s, c) => s + (c.amount || 0), 0);

  // By category
  const byCat = {};
  filtered.forEach(c => {
    const cat = c.category || 'lain';
    byCat[cat] = (byCat[cat] || 0) + (c.amount || 0);
  });
  const catData = Object.entries(byCat)
    .map(([cat, amount]) => ({ name: CATEGORY_LABELS[cat] || cat, amount }))
    .sort((a, b) => b.amount - a.amount);

  // By date
  const byDate = {};
  filtered.forEach(c => {
    const d = new Date(c.created_at);
    const key = period === 'month'
      ? `${d.getDate()}/${d.getMonth() + 1}`
      : ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'][d.getMonth()];
    byDate[key] = (byDate[key] || 0) + (c.amount || 0);
  });
  const dateData = Object.entries(byDate).map(([name, amount]) => ({ name, amount }));

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-manrope">{t('Laporan Biaya', 'Costs Report')}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{t('Statistik pengeluaran operasional toko', 'Store operational expense statistics')}</p>
          </div>
          <div className="flex gap-1.5 bg-slate-100 p-1 rounded-xl">
            {[['month','Bulan Ini'],['year','Tahun Ini'],['all','Semua']].map(([val, lbl]) => (
              <button key={val} onClick={() => setPeriod(val)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${period === val ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-700'}`}>
                {lbl}
              </button>
            ))}
          </div>
        </div>

        {/* Summary card */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
              <TrendingDown size={26} className="text-red-500" />
            </div>
            <div>
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">{t('Total Pengeluaran', 'Total Expenses')}</p>
              <p className="text-3xl font-bold text-slate-900 font-manrope">{formatCurrency(total)}</p>
              <p className="text-xs text-slate-400 mt-0.5">{filtered.length} {t('transaksi', 'transactions')}</p>
            </div>
          </div>
        </div>

        {/* Charts */}
        {filtered.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <h3 className="font-semibold text-slate-700 mb-4 text-sm">{t('Tren Pengeluaran', 'Expense Trend')}</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={dateData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [formatCurrency(v), 'Biaya']} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                  <Bar dataKey="amount" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
              <h3 className="font-semibold text-slate-700 mb-4 text-sm">{t('Per Kategori', 'By Category')}</h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={catData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [formatCurrency(v), 'Biaya']} contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {catData.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Detail table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="p-5 border-b border-slate-50">
            <h3 className="font-semibold text-slate-800 text-sm font-manrope">{t('Rincian', 'Details')}</h3>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-7 h-7 border-2 border-slate-200 border-t-[#0f6c44] rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Receipt size={32} className="text-slate-200 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">{t('Belum ada data pengeluaran', 'No expense data')}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {filtered.map((c, i) => (
                <div key={c.id || i} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50/50">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{c.description}</p>
                    <p className="text-xs text-slate-400">{CATEGORY_LABELS[c.category] || c.category} · {formatDate(c.created_at)}</p>
                  </div>
                  <p className="font-bold text-red-600 text-sm">{formatCurrency(c.amount)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
